<script setup lang="ts">
import { classNames } from '@libero/utilities/class-names';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { StepItem } from './Step.types';

export interface Props {
  value: number;
  items: StepItem[];
  isSticky?: boolean;
}

const props = defineProps<Props>();

const { t } = useI18n();

const progress = computed(() => (props.value / (props.items.length - 1)) * 100);
</script>

<template>
  <div class="steps" :class="classNames({ isSticky })">
    <div class="steps-content">
      <div class="steps-slider" :style="{ transform: `translateX(-${value * 100}%)` }">
        <div
          v-for="(item, index) in items"
          :key="item.title"
          class="steps-item"
          :class="
            classNames({
              isActive: index === value,
              isDone: index < value || value === items.length - 1,
            })
          "
          :ariaCurrent="index === value ? 'step' : undefined"
        >
          <div class="steps-item-content">
            <div v-if="index === items.length - 1" class="steps-item-count">
              {{ t('finished') }}
            </div>

            <div v-else class="steps-item-count">
              {{ t('step', { number: index + 1 }) }}

              <span class="steps-item-count-total">
                {{ `/ ${items.length - 1}` }}
              </span>
            </div>

            <div class="steps-item-icon">
              <component :is="item.icon" />
            </div>

            <div class="steps-item-indicator" />

            <div class="steps-item-title">
              {{ item.title }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="steps-track">
      <span class="steps-track-progress" :style="{ width: `${progress}%` }" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@libero/ui-framework/Steps/Steps.scss';
</style>
